/**
 *
 * @module atvPlayerAdapter
 * @desc PlayerAdapter for legacy Apple TV
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/stream-sample.md
 * @see https://github.bamtech.co/fed-ce/hbo-appletv-app/blob/master/source/js/app/App.SecureKeyDelivery.js#L241
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerEvents.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerProperties.md
 * @see https://github.bamtech.co/fed-ce/mls-appletv-app/blob/master/docs/apple_atv_legacy.pdf
 *
 */

import Check from 'check-types';
import Services from './../../services/entry';
import Drm from './../../drm/entry';
import PlayerAdapter from './../playerAdapter';
import PlaybackMetrics from './../playbackMetrics';
import PlaybackEventListener from './../playbackEventListener';

const Util = Services.Util;
const DrmProvider = Drm.DrmProvider;

const ErrorReason = Services.Exception.ErrorReason;
const ExceptionReference = Services.Exception.ExceptionReference;
const ServiceException = Services.Exception.ServiceException;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    object,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class AtvPlayerAdapter
 * @extends {PlayerAdapter}
 * @desc Interface used to communicate with the media player.
 *
 */
export default class AtvPlayerAdapter extends PlayerAdapter {

    /**
     *
     * @param {NativePlayer} nativePlayer
     * @param {atv.secureKeyDelivery} secureKeyDelivery
     * @param {Object|Null} [busReference=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(nativePlayer, secureKeyDelivery, busReference = null) {

        const errorMessage = {
            ctor: 'AtvPlayerAdapter(nativePlayer, secureKeyDelivery, busReference)',
            get nativePlayer() {
                return `${this.ctor} needs NativePlayer instance`;
            },
            get secureKeyDelivery() {
                return `${this.ctor} needs atv.secureKeyDelivery instance`;
            },
            get busReference() {
                return `${this.ctor} needs plain BusReference instance`;
            }
        };

        Check.assert.function(Util.getSafe(() => nativePlayer.playerStateChanged), errorMessage.nativePlayer, InvalidArgumentException); // eslint-disable-line
        object(secureKeyDelivery, errorMessage.secureKeyDelivery, InvalidArgumentException);

        if (Check.assigned(busReference)) {

            Check.assert.function(busReference.handler, errorMessage.busReference, InvalidArgumentException); // eslint-disable-line
            Check.assert.function(busReference.releaseHandler, errorMessage.busReference, InvalidArgumentException); // eslint-disable-line

        }

        super(nativePlayer);

        /**
         *
         * @access private
         * @type {Number}
         * @desc The elapsed time, in seconds, of the current playhead position.
         *
         */
        this.currentPlayhead = null;

        /**
         *
         * @access private
         * @type {atv.secureKeyDelivery}
         * @since 3.3.3
         *
         */
        this.secureKeyDelivery = secureKeyDelivery;

        /**
         *
         * @access private
         * @type {Object|Null}
         * @since 2.2.0
         *
         */
        this.busReference = busReference;

    }

    /**
     *
     * @access public
     * @param {String} playlistUri - The URL to be used during playback.
     * @desc Callback used when prepare has been called (usually via the {PlaybackSession}).
     * Sets the source URI on the {NativePlayer} instance.
     * @returns {Promise<Void>}
     *
     */
    setSource(playlistUri) {

        const errorMessage = {
            method: 'AtvPlayerAdapter.setSource(playlistUri)',
            get playlistUri() {
                return `${this.method} playlistUri should be a String with length`;
            }
        };

        nonEmptyString(playlistUri, errorMessage.playlistUri, InvalidArgumentException);

        return new Promise((resolve) => {

            this.playlistUri = playlistUri;

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @desc Gets a snapshot of information about media playback.
     * @throws {InvalidStateException} Unable to get NativePlayer playhead data
     * @returns {Promise<PlaybackMetrics>} - instance that contains a snapshot
     * of information about media playback.
     * @note metric value is rounded down to prevent possible service issues with floats
     * @note executed by {PlaybackTelemetryDispatcher#recordStreamSample}
     *
     */
    getPlaybackMetrics() {

        const errorMessage = {
            method: 'AtvPlayerAdapter.getPlaybackMetrics()',
            get currentPlayhead() {
                return `${this.method} unable to get NativePlayer playhead data`;
            }
        };

        const { nativePlayer, unavailableMetric } = this;

        if (Check.not.function(nativePlayer.playerTimeDidChange)) {

            const reasons = [new ErrorReason('', errorMessage.currentPlayhead)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        const currentBitrate = unavailableMetric;
        const currentPlayhead = Math.floor(this.currentPlayhead) || unavailableMetric;

        return Promise.resolve(new PlaybackMetrics(currentBitrate, currentPlayhead));

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @throws {InvalidStateException} Unable to add PlaybackEventListener
     * @returns {Promise<Void>}
     *
     */
    addListener(listener) {

        const errorMessage = {
            method: 'AtvPlayerAdapter.addListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get addListener() {
                return `${this.method} unable to add PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer, boundHandlers, busReference } = this;
        const { onPlay, onPause, onError, onPlayedToCompletion } = this;
        const { onPlayerTimeDidChange } = this;

        return new Promise((resolve, reject) => {

            if (Check.function(nativePlayer.playerTimeDidChange)) {

                this.listener = listener;

                boundHandlers.onPlay = onPlay.bind(this);
                boundHandlers.onPause = onPause.bind(this);
                boundHandlers.onError = onError.bind(this);
                boundHandlers.onPlayedToCompletion = onPlayedToCompletion.bind(this);
                boundHandlers.onPlayerTimeDidChange = onPlayerTimeDidChange.bind(this);

                boundHandlers.playerStateChanged = (playerState = {}) => {

                    const { newState } = playerState;

                    if (newState === nativePlayer.states.Paused) {
                        return boundHandlers.onPause.call(this);
                    }

                    return undefined;

                };

                if (Check.assigned(busReference)) {

                    busReference.handler(
                        'atv.player.willStartPlaying',
                        boundHandlers.onPlay
                    );

                    busReference.handler(
                        'atv.player.playerStateChanged',
                        boundHandlers.playerStateChanged
                    );

                    busReference.handler(
                        'atv.player.onPlaybackError',
                        boundHandlers.onError
                    );

                    busReference.handler(
                        'atv.player.didStopPlaying',
                        boundHandlers.onPlayedToCompletion
                    );

                    busReference.handler(
                        'atv.player.playerTimeDidChange',
                        boundHandlers.onPlayerTimeDidChange
                    );

                    return resolve();

                }

                nativePlayer.willStartPlaying = boundHandlers.onPlay;
                nativePlayer.didStopPlaying = boundHandlers.onPause;
                nativePlayer.onPlaybackError = boundHandlers.onError;
                nativePlayer.didStopPlaying = boundHandlers.onPlayedToCompletion;
                nativePlayer.playerTimeDidChange = boundHandlers.onPlayerTimeDidChange;

                return resolve();

            }

            const reasons = [new ErrorReason('', errorMessage.addListener)];
            const exceptionData = ExceptionReference.common.invalidState;

            return reject(new ServiceException({ reasons, exceptionData }));

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @returns {Promise<Void>}
     *
     */
    removeListener(listener) {

        const errorMessage = {
            method: 'AtvPlayerAdapter.removeListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer, busReference, boundHandlers } = this;

        return new Promise((resolve) => {

            if (Check.function(nativePlayer.playerTimeDidChange) && Check.equal(this.listener, listener)) {

                this.listener = null;

                if (Check.assigned(busReference)) {

                    busReference.releaseHandler(
                        'atv.player.willStartPlaying',
                        boundHandlers.onPlay
                    );

                    busReference.releaseHandler(
                        'atv.player.playerStateChanged',
                        boundHandlers.playerStateChanged
                    );

                    busReference.releaseHandler(
                        'atv.player.onPlaybackError',
                        boundHandlers.onError
                    );

                    busReference.releaseHandler(
                        'atv.player.didStopPlaying',
                        boundHandlers.onPlayedToCompletion
                    );

                    busReference.releaseHandler(
                        'atv.player.playerTimeDidChange',
                        boundHandlers.onPlayerTimeDidChange
                    );

                    return resolve();

                }

                nativePlayer.willStartPlaying = null;
                nativePlayer.didStopPlaying = null;
                nativePlayer.onPlaybackError = null;
                nativePlayer.didStopPlaying = null;
                nativePlayer.playerTimeDidChange = null;

            }

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.2.0
     * @param {SDK.Drm.DrmProvider} drmProvider
     * @returns {Promise<Void>}
     *
     */
    setDrmProvider(drmProvider) {

        const errorMessage = {
            method: 'AtvPlayerAdapter.setDrmProvider(drmProvider)',
            get drmProvider() {
                return `${this.method} needs SDK.Drm.DrmProvider instance`;
            }
        };

        instanceStrict(drmProvider, DrmProvider, errorMessage.drmProvider, InvalidArgumentException);

        this.drmProvider = drmProvider;

        return this.attach();

    }

    /**
     *
     * @access public
     * @desc Resets player adapter state. Removes all playback listeners.
     * @returns {Promise<Void>}
     *
     */
    clean() {

        const { listener } = this;

        this.playlistUri = '';
        this.boundHandlers = {};
        this.currentPlayhead = null;
        this.drmProvider = null;

        if (Check.assigned(listener)) {
            return this.removeListener(listener);
        }

        return Promise.resolve();

    }

    /* #region private: */

    /**
     *
     * @access public
     * @since 3.1.0
     * @desc Completes the cleanup process by completely cleaning up all `PlayerAdapter`
     * references. This method should be executed by the application developer
     * when they no longer need to use a `PlayerAdapter` instance.
     * @note Because `clean` is automatically called by the `PlaybackSession` instance,
     * another method is necessary to handle the use case of an application developer who
     * wants to continue using a `PlayerAdapter` instance after a `PlaybackSession` has been released.
     * @returns {Promise<Void>}
     *
     */
    dispose() {

        this.nativePlayer = null;
        this.accessToken = null;
        this.secureKeyDelivery = null;
        this.busReference = null;

        return Promise.resolve();

    }

    /**
     *
     * @access private
     * @since 2.0.0
     * @param {Object} time
     * @desc called whenever the playhead time changes for the currently playing asset.
     * @returns {Promise<Void>}
     *
     */
    onPlayerTimeDidChange(time) {

        return new Promise((resolve) => {

            this.currentPlayhead = time.timeIntervalSec;

            return resolve();

        });

    }

    /**
     *
     * @access private
     * @since 3.3.0
     * @returns {Promise<Void>}
     *
     */
    attach() {

        const { secureKeyDelivery } = this;

        secureKeyDelivery.cancelAllRequests = this.cancelAllRequests.bind(this);
        secureKeyDelivery.fetchCertificate = this.fetchCertificate.bind(this);
        secureKeyDelivery.fetchAssetID = this.fetchAssetId.bind(this);
        secureKeyDelivery.fetchKeys = this.fetchKeys.bind(this);

        return Promise.resolve();

    }

    /**
     *
     * @access private
     * @since 3.3.3
     * @note: This method needs to be around in order for atv to call even if it returns nothing
     * @returns {Void}
     *
     */
    cancelAllRequests() {
    }

    /**
     *
     * @access private
     * @since 3.3.3
     * @returns {Void}
     *
     */
    fetchCertificate(uri, callback) {

        const { drmProvider } = this;

        drmProvider.getFairPlayCertificate().then((certificate) => {

            callback.success(certificate);

        }).catch((ex) => {

            callback.failure(ex);

        });

    }

    /**
     *
     * @access private
     * @since 3.3.3
     * @returns {Void}
     *
     */
    fetchAssetId(uri, callback) {

        const assetId = this.assetIdFromUri(uri);

        if (Check.assigned(assetId)) {
            callback.success(assetId, false);
        }

    }

    /**
     *
     * @access private
     * @since 3.3.3
     * @returns {Void}
     *
     */
    fetchKeys(uri, requestData, callback) {

        const { drmProvider } = this;
        const serverContext = JSON.stringify({ spc: requestData });

        drmProvider.getFairPlayLicense(serverContext).then((license) => {

            callback.success(license);

        }).catch((ex) => {

            callback.failure(ex);

        });

    }

    /**
     *
     * @access private
     * @since 3.3.3
     * @returns {String}
     *
     */
    assetIdFromUri(uri) {

        const scheme = 'skd://';
        const schemeLoc = uri.indexOf(scheme);

        let assetID = null;

        if (schemeLoc > -1) {
            assetID = uri.substring(scheme.length);
        }

        return assetID;

    }

}



// WEBPACK FOOTER //
// ./src/media/playerAdapter/atvPlayerAdapter.js